import classNames from "classnames";
import { usePrevious } from "@/hooks/usePrevious";
import { bakeImage } from "@/lib/oa/factories";
import { useEffect, useRef, useState } from "react";

export const Image = ({
  image,
  bakeSettings,
  fill,
  width,
  height,
  className,
  skeleton = true,
  ...props
}: {
  image?: Travelguide.Image;
  bakeSettings: Parameter<typeof bakeImage, 1>;
  fill?: boolean;
  width?: number | string;
  height?: number | string;
  className?: string;
  skeleton?: boolean;
}) => {
  const [loaded, setLoaded] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);
  const prevImage = usePrevious(image);

  const size: Record<string, string> = {};

  if (width) {
    size.width =
      typeof width == "number" ? `w-[${width}px]` : (width as string);
  }
  if (height) {
    size.height =
      typeof height == "number" ? `h-[${height}px]` : (height as string);
  }

  const { src = "", alt = "Bild fehlt" } = image
    ? bakeImage(image, bakeSettings)
    : {};

  useEffect(() => {
    if (!imgRef.current) {
      return;
    }
    setLoaded(imgRef.current.complete);
  }, [imgRef]);

  useEffect(() => {
    if (image && prevImage && image.id !== prevImage.id) {
      setLoaded(false);
    }
  }, [image]);

  if (skeleton) {
    return (
      <div
        className={classNames(className, "relative", {
          [`object-cover w-full h-full`]: fill,
          [size.width]: !fill && size.width,
          [size.height]: !fill && size.height,
        })}
      >
        <div className="absolute t-0 l-0 w-full h-full bg-[#e0e0e0]"></div>
        <img
          ref={imgRef}
          onLoad={() => setLoaded(true)}
          decoding="async"
          className={classNames(
            "relative w-full h-full object-cover",
            "opacity-0 transition-opacity delay-300",
            {
              "opacity-100": loaded,
            }
          )}
          src={src}
          alt={alt}
          {...props}
        />
      </div>
    );
  }

  return (
    <img
      decoding="async"
      className={classNames(className, {
        [`object-cover w-full h-full`]: fill,
        [size.width]: !fill && size.width,
        [size.height]: !fill && size.height,
      })}
      src={src}
      alt={alt}
      {...props}
    />
  );
};
